import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList, animeCategories } from "@/mocks/anime";

const sortOptions = [
  { key: "rating", label: "按评分" },
  { key: "episodeCount", label: "按集数" },
  { key: "year", label: "按年份" },
];

export default function Library() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") ?? "全部";
  const [sort, setSort] = useState("rating");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    let list = animeList;
    if (activeCat !== "全部") {
      list = list.filter((a) => a.category === activeCat);
    }
    if (keyword.trim()) {
      const kw = keyword.trim();
      list = list.filter(
        (a) =>
          a.name.includes(kw) ||
          a.tags.some((t) => t.includes(kw)) ||
          a.desc.includes(kw),
      );
    }
    return [...list].sort((a, b) => {
      if (sort === "episodeCount") return b.episodeCount - a.episodeCount;
      if (sort === "year") return b.year.localeCompare(a.year);
      return b.rating - a.rating;
    });
  }, [activeCat, keyword, sort]);

  const changeCat = (key: string) => {
    if (key === "全部") {
      params.delete("cat");
    } else {
      params.set("cat", key);
    }
    setParams(params, { replace: true });
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-24 md:pt-28">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-8 md:py-12">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <a href="#/" className="hover:text-primary-700">首页</a>
              <span className="mx-1.5">/</span>
              <span className="text-foreground-700">番剧库</span>
            </nav>
            <h1 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">番剧库</h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">
              精选泡面番、三分钟短番与日常搞笑下饭番，按口味筛选，找到属于你的那部下饭神番。
            </p>

            <div className="mt-7 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                {animeCategories.map((cat) => (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => changeCat(cat.key)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                      activeCat === cat.key
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-800"
                    }`}
                  >
                    <i className={cat.icon} aria-hidden="true" />
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative">
                  <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-foreground-400" aria-hidden="true" />
                  <input
                    type="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索番名 / 标签"
                    aria-label="搜索番剧"
                    className="h-10 w-full rounded-full border border-background-200 bg-background-50 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300 sm:w-56"
                  />
                </div>
                <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSort(opt.key)}
                      className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
                        sort === opt.key
                          ? "bg-background-50 text-primary-800 font-medium shadow-sm"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 md:px-6 pb-16 md:pb-20">
          <p className="mb-5 text-sm text-foreground-500">
            共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部{activeCat !== "全部" ? `「${activeCat}」` : ""}短番
          </p>
          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
              {filtered.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <i className="ri-folder-open-line text-4xl text-foreground-300" aria-hidden="true" />
              <p className="mt-3 text-sm text-foreground-500">没有找到匹配的短番，换个关键词试试吧</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}